import { useState } from "react";
import {
  DateTimeField,
  ErrorCodes,
  ProblemError,
  hasGrant,
  usePrincipal,
} from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import {
  ASOF_PATTERN,
  CARRIER_TYPES,
  CODE_PATTERN,
  OPERATIONAL_STATUSES,
  type AddBranchAuthorizationInput,
  type CarrierType,
  type CarrierVersionInput,
  type CarrierView,
  type CreateCarrierInput,
  type OperationalStatus,
} from "../../api/catalog-contracts";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LoadingState,
  Select,
  StatusLabel,
  Table,
  TableCard,
} from "../../ui";
import type { StatusTone } from "../../ui";
import { useCarriers } from "./use-catalog";
import {
  useAddAuthorization,
  useAddVersion,
  useAuthorizations,
  useCarrierVersions,
  useCatalogLines,
  useCreateCarrier,
} from "./use-catalog-mgmt";
import { ProductManagement } from "./product-management";
import { PackageManagement } from "./package-management";
import { ConceptCoverageManagement } from "./concept-coverage-management";

// The Katalog Yönetimi surface — the admin self-service catalog console. Four governed
// sections behind a calm tab bar: carriers (U9), products, packages, and concepts +
// coverages (catalog step 2, ADR 0004 §§4-5). Every write re-enforces catalog:write @
// all server-side; the tabs only group the surfaces. Carriers is the default tab.
type CatalogTab = "carriers" | "products" | "packages" | "concepts";

export function CatalogManagement() {
  const { t } = useT();
  const { me } = usePrincipal();
  const canWrite = hasGrant(me, "catalog:write", "all");
  const [tab, setTab] = useState<CatalogTab>("carriers");

  const tabs: Array<{ key: CatalogTab; label: string }> = [
    { key: "carriers", label: t("catmgmt.tab.carriers") },
    { key: "products", label: t("catmgmt.tab.products") },
    { key: "packages", label: t("catmgmt.tab.packages") },
    { key: "concepts", label: t("catmgmt.tab.concepts") },
  ];

  return (
    <div className="surf-stack">
      <div className="cat-tabbar" role="tablist" aria-label={t("catmgmt.tabsLabel")}>
        {tabs.map((tb) => (
          <button
            key={tb.key}
            type="button"
            role="tab"
            aria-selected={tab === tb.key}
            className={`cat-tab${tab === tb.key ? " active" : ""}`}
            data-testid={`catmgmt-tab-${tb.key}`}
            onClick={() => setTab(tb.key)}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === "carriers" && <CarriersPanel />}
      {tab === "products" && <ProductManagement canWrite={canWrite} />}
      {tab === "packages" && <PackageManagement canWrite={canWrite} />}
      {tab === "concepts" && <ConceptCoverageManagement canWrite={canWrite} />}
    </div>
  );
}

// Map the operational-status vocabulary onto the founder's status-label tones
// (11-rozetler-etiketler): the color is the meaning, the copy stays the i18n label.
const STATUS_TONE: Record<OperationalStatus, StatusTone> = {
  ACTIVE: "active",
  SUSPENDED: "expiring",
  WITHDRAWN: "ended",
};

// Catalog management — the admin self-service surface: view carriers, add a carrier,
// append a version (governed, append-only — ADR 0004 §2), and record a branch (line)
// authorization. No business logic in the browser: the versioning contract is
// server-enforced (the DB refuses in-place edits); this UI appends whole states and
// surfaces the server's ProblemJson. RBAC is UX — the write affordances are gated on
// catalog:write @ all; the API re-enforces every call regardless.
function CarriersPanel() {
  const { t } = useT();
  const { me } = usePrincipal();
  const canWrite = hasGrant(me, "catalog:write", "all");
  const carriers = useCarriers();
  const [selected, setSelected] = useState<string | null>(null);

  return (
    <section className="surf-stack">
      <div className="surf-head">
        <div>
          <h1 className="surf-title">{t("cat.heading")}</h1>
          <p className="surf-sub muted">{t("cat.carrier.governedNote")}</p>
        </div>
      </div>

      <TableCard title={t("cat.list.title")} data-testid="cat-list">
        {carriers.isLoading && <LoadingState label={t("cat.loading")} rows={3} />}
        {carriers.error instanceof ProblemError && (
          <ErrorState title={t("cat.carriersError")} description={carriers.error.problem.detail} />
        )}
        {carriers.data && carriers.data.data.length === 0 && (
          <EmptyState data-testid="cat-empty" icon="records" title={t("cat.empty")} />
        )}
        {carriers.data && carriers.data.data.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>{t("cat.list.code")}</th>
                <th>{t("cat.list.tradeName")}</th>
                <th>{t("cat.list.type")}</th>
                <th>{t("cat.list.status")}</th>
                <th className="cat-td-right" />
              </tr>
            </thead>
            <tbody>
              {carriers.data.data.map((c) => {
                const status = c.current.operationalStatus as OperationalStatus;
                return (
                  <tr key={c.id} data-testid="cat-carrier-row">
                    <td>
                      <code>{c.code}</code>
                    </td>
                    <td className="cat-strong">{c.current.tradeName}</td>
                    <td>{t(`cat.type.${c.current.carrierType as CarrierType}`)}</td>
                    <td>
                      <StatusLabel tone={STATUS_TONE[status]}>
                        {t(`cat.status.${status}`)}
                      </StatusLabel>
                    </td>
                    <td className="cat-td-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        data-testid="cat-detail-btn"
                        onClick={() => setSelected((s) => (s === c.code ? null : c.code))}
                      >
                        {t("cat.detail")}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </TableCard>

      {selected && (
        <CarrierDetail
          code={selected}
          carrier={carriers.data?.data.find((c) => c.code === selected) ?? null}
          canWrite={canWrite}
        />
      )}

      <AddCarrierForm canWrite={canWrite} />
    </section>
  );
}

// The carrier detail: version history (append-only) + branch authorizations, each with
// its governed write form. The current version is the one with effectiveTo === null.
function CarrierDetail({
  code,
  carrier,
  canWrite,
}: {
  code: string;
  carrier: CarrierView | null;
  canWrite: boolean;
}) {
  const { t } = useT();
  return (
    <div className="cat-detail surf-stack" data-testid="cat-detail">
      <div className="cat-detail-head">
        <span className="cat-detail-eyebrow muted">{t("cat.detailEyebrow")}</span>
        <h2 className="cat-detail-name">
          {carrier ? carrier.current.tradeName : code} <code>{code}</code>
        </h2>
      </div>

      <VersionHistory code={code} canWrite={canWrite} />
      <BranchAuthorizations code={code} canWrite={canWrite} />
    </div>
  );
}

function VersionHistory({ code, canWrite }: { code: string; canWrite: boolean }) {
  const { t } = useT();
  const versions = useCarrierVersions(code);

  return (
    <>
      <TableCard
        title={t("cat.versions.heading")}
        footer={<span className="muted surf-foot-note">{t("cat.versions.appendOnlyNote")}</span>}
      >
        {versions.isLoading && <LoadingState label={t("cat.versions.loading")} rows={2} />}
        {versions.error instanceof ProblemError && (
          <ErrorState
            title={t("cat.versions.loading")}
            description={versions.error.problem.detail}
          />
        )}
        {versions.data && versions.data.data.length === 0 && (
          <EmptyState
            data-testid="cat-versions-empty"
            icon="records"
            title={t("cat.versions.empty")}
          />
        )}
        {versions.data && versions.data.data.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>{t("cat.versions.version")}</th>
                <th>{t("cat.list.tradeName")}</th>
                <th>{t("cat.versions.legalName")}</th>
                <th>{t("cat.list.type")}</th>
                <th>{t("cat.list.status")}</th>
                <th>{t("cat.versions.window")}</th>
              </tr>
            </thead>
            <tbody>
              {versions.data.data.map((v) => (
                <tr key={v.id} data-testid="cat-version-row" data-open={v.effectiveTo === null}>
                  <td className="cat-strong">v{v.version}</td>
                  <td>{v.tradeName}</td>
                  <td>{v.legalName}</td>
                  <td>{t(`cat.type.${v.carrierType}`)}</td>
                  <td>
                    <StatusLabel tone={STATUS_TONE[v.operationalStatus]}>
                      {t(`cat.status.${v.operationalStatus}`)}
                    </StatusLabel>
                  </td>
                  <td>
                    {v.effectiveTo === null ? (
                      <strong className="cat-strong">{t("cat.versions.current")}</strong>
                    ) : (
                      <span className="muted">
                        {v.effectiveFrom} → {v.effectiveTo}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableCard>

      <AddVersionForm code={code} canWrite={canWrite} />
    </>
  );
}

// Append a new carrier version — the FULL next-state, never a patch. The server closes
// the open version and opens this one in one tx (append-only, ADR 0004 §2).
function AddVersionForm({ code, canWrite }: { code: string; canWrite: boolean }) {
  const { t } = useT();
  const add = useAddVersion(code);
  const [tradeName, setTradeName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [carrierType, setCarrierType] = useState<CarrierType>("CONVENTIONAL");
  const [operationalStatus, setOperationalStatus] = useState<OperationalStatus>("ACTIVE");

  const canSubmit =
    canWrite && tradeName.trim() !== "" && legalName.trim() !== "" && !add.isPending;

  function submit() {
    const body: CarrierVersionInput = {
      tradeName: tradeName.trim(),
      legalName: legalName.trim(),
      carrierType,
      operationalStatus,
    };
    add.mutate(body);
  }

  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("cat.versions.add")}</span>
        <span className="cat-append-pill">
          <Icon name="lock" size={12} strokeWidth={1.9} />
          {t("cat.appendOnlyTag")}
        </span>
      </div>
      {!canWrite && <p className="muted surf-note">{t("cat.readOnly")}</p>}
      <p className="muted surf-note">{t("cat.versions.appendOnlyNote")}</p>

      <form
        data-testid="cat-version-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit();
        }}
      >
        <fieldset className="k-form-grid" disabled={!canWrite}>
          <Input
            label={t("cat.list.tradeName")}
            value={tradeName}
            onChange={(e) => setTradeName(e.target.value)}
          />
          <Input
            label={t("cat.versions.legalName")}
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
          />
          <TypeSelect value={carrierType} onChange={setCarrierType} />
          <StatusSelect value={operationalStatus} onChange={setOperationalStatus} />
        </fieldset>

        <div className="k-form-actions">
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={add.isPending}>
            {t("cat.versions.submit")}
          </Button>
          {add.isSuccess && (
            <span role="status" className="form-ok">
              {t("cat.versions.success")}
            </span>
          )}
          {add.error instanceof ProblemError && (
            <span role="alert" className="form-error">
              {add.error.problem.detail}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function BranchAuthorizations({ code, canWrite }: { code: string; canWrite: boolean }) {
  const { t } = useT();
  const auths = useAuthorizations(code);
  const lines = useCatalogLines();
  const lineName = (c: string) => lines.data?.data.find((l) => l.code === c)?.name ?? c;

  return (
    <>
      <TableCard title={t("cat.auth.heading")}>
        {auths.isLoading && <LoadingState label={t("cat.auth.loading")} rows={2} />}
        {auths.error instanceof ProblemError && (
          <ErrorState title={t("cat.auth.loading")} description={auths.error.problem.detail} />
        )}
        {auths.data && auths.data.data.length === 0 && (
          <EmptyState data-testid="cat-auths-empty" icon="records" title={t("cat.auth.empty")} />
        )}
        {auths.data && auths.data.data.length > 0 && (
          <Table>
            <thead>
              <tr>
                <th>{t("cat.auth.line")}</th>
                <th>{t("cat.auth.from")}</th>
                <th>{t("cat.auth.to")}</th>
              </tr>
            </thead>
            <tbody>
              {auths.data.data.map((a) => (
                <tr key={a.id} data-testid="cat-auth-row" data-open={a.authorizedTo === null}>
                  <td>
                    <span className="cat-strong">{lineName(a.lineCode)}</span>{" "}
                    <code>{a.lineCode}</code>
                  </td>
                  <td>{a.authorizedFrom}</td>
                  <td>
                    {a.authorizedTo === null ? (
                      <StatusLabel tone="active">{t("cat.auth.open")}</StatusLabel>
                    ) : (
                      <span className="muted">{a.authorizedTo}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </TableCard>

      <AddAuthorizationForm code={code} canWrite={canWrite} />
    </>
  );
}

// Record a branch (line) authorization. The line picker is GET /v1/catalog/lines; an
// already-open authorization for the same line returns 409 (surfaced inline).
function AddAuthorizationForm({ code, canWrite }: { code: string; canWrite: boolean }) {
  const { t } = useT();
  const add = useAddAuthorization(code);
  const lines = useCatalogLines();
  const [lineCode, setLineCode] = useState("");
  const [authorizedTo, setAuthorizedTo] = useState("");

  const toInvalid = authorizedTo.trim() !== "" && !ASOF_PATTERN.test(authorizedTo.trim());
  const canSubmit = canWrite && lineCode !== "" && !toInvalid && !add.isPending;

  function submit() {
    const body: AddBranchAuthorizationInput = { lineCode };
    if (authorizedTo.trim()) body.authorizedTo = authorizedTo.trim();
    add.mutate(body);
  }

  const isConflict = add.error instanceof ProblemError && add.error.is(ErrorCodes.CONFLICT);

  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("cat.auth.add")}</span>
      </div>
      {!canWrite && <p className="muted surf-note">{t("cat.readOnly")}</p>}

      <form
        data-testid="cat-auth-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit();
        }}
      >
        <fieldset className="k-form-grid" disabled={!canWrite}>
          <Select
            label={t("cat.auth.line")}
            value={lineCode}
            onChange={(e) => setLineCode(e.target.value)}
          >
            <option value="">—</option>
            {(lines.data?.data ?? []).map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </Select>
          {lines.error != null && (
            <p className="form-error" role="alert" data-testid="lines-load-failed">
              {t("cat.linesLoadFailed")}
            </p>
          )}
          <DateTimeField
            label={t("cat.auth.to")}
            value={authorizedTo}
            onChange={setAuthorizedTo}
            todayLabel={t("df.today")}
            nowLabel={t("df.now")}
            error={toInvalid ? t("cat.auth.invalidTo") : undefined}
            hint={!toInvalid ? t("cat.auth.toHint") : undefined}
            data-testid="cat-auth-to"
          />
        </fieldset>

        <div className="k-form-actions">
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={add.isPending}>
            {t("cat.auth.submit")}
          </Button>
          {add.isSuccess && (
            <span role="status" className="form-ok">
              {t("cat.auth.success")}
            </span>
          )}
          {isConflict && (
            <span role="alert" className="form-error" data-testid="cat-auth-conflict">
              {t("cat.auth.conflict")}
            </span>
          )}
          {add.error instanceof ProblemError && !isConflict && (
            <span role="alert" className="form-error">
              {add.error.problem.detail}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

// Add a carrier — identity + version 1. The code is the immutable identity (lowercase-
// kebab); an already-existing code returns 409 (surfaced inline).
function AddCarrierForm({ canWrite }: { canWrite: boolean }) {
  const { t } = useT();
  const create = useCreateCarrier();
  const [code, setCode] = useState("");
  const [tradeName, setTradeName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [carrierType, setCarrierType] = useState<CarrierType>("CONVENTIONAL");
  const [operationalStatus, setOperationalStatus] = useState<OperationalStatus>("ACTIVE");

  const codeInvalid = code.trim() !== "" && !CODE_PATTERN.test(code.trim());
  const canSubmit =
    canWrite &&
    code.trim() !== "" &&
    !codeInvalid &&
    tradeName.trim() !== "" &&
    legalName.trim() !== "" &&
    !create.isPending;

  function submit() {
    const body: CreateCarrierInput = {
      code: code.trim(),
      tradeName: tradeName.trim(),
      legalName: legalName.trim(),
      carrierType,
      operationalStatus,
    };
    create.mutate(body);
  }

  const isConflict = create.error instanceof ProblemError && create.error.is(ErrorCodes.CONFLICT);

  return (
    <Card className="cat-formcard">
      <div className="surf-cardhead">
        <span className="surf-cardtitle">{t("cat.carrier.add")}</span>
      </div>
      {!canWrite && <p className="muted surf-note">{t("cat.readOnly")}</p>}
      <p className="muted surf-note">{t("cat.carrier.governedNote")}</p>

      <form
        data-testid="cat-carrier-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) submit();
        }}
      >
        <fieldset className="k-form-grid" disabled={!canWrite}>
          <Input
            label={t("cat.list.code")}
            placeholder="allianz-sigorta"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            hint={t("cat.carrier.codeHint")}
            error={codeInvalid ? t("cat.carrier.invalidCode") : undefined}
            errorTestId="cat-err-code"
          />
          <Input
            label={t("cat.list.tradeName")}
            value={tradeName}
            onChange={(e) => setTradeName(e.target.value)}
          />
          <Input
            label={t("cat.versions.legalName")}
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
          />
          <TypeSelect value={carrierType} onChange={setCarrierType} />
          <StatusSelect value={operationalStatus} onChange={setOperationalStatus} />
        </fieldset>

        <div className="k-form-actions">
          <Button variant="primary" type="submit" disabled={!canSubmit} loading={create.isPending}>
            {t("cat.carrier.submit")}
          </Button>
          {create.isSuccess && (
            <span role="status" className="form-ok">
              {t("cat.carrier.success")}
            </span>
          )}
          {isConflict && (
            <span role="alert" className="form-error" data-testid="cat-carrier-conflict">
              {t("cat.carrier.conflict")}
            </span>
          )}
          {create.error instanceof ProblemError && !isConflict && (
            <span role="alert" className="form-error">
              {create.error.problem.detail}
            </span>
          )}
        </div>
      </form>
    </Card>
  );
}

function TypeSelect({
  value,
  onChange,
}: {
  value: CarrierType;
  onChange: (v: CarrierType) => void;
}) {
  const { t } = useT();
  return (
    <Select
      label={t("cat.list.type")}
      value={value}
      onChange={(e) => onChange(e.target.value as CarrierType)}
    >
      {CARRIER_TYPES.map((tp) => (
        <option key={tp} value={tp}>
          {t(`cat.type.${tp}`)}
        </option>
      ))}
    </Select>
  );
}

function StatusSelect({
  value,
  onChange,
}: {
  value: OperationalStatus;
  onChange: (v: OperationalStatus) => void;
}) {
  const { t } = useT();
  return (
    <Select
      label={t("cat.list.status")}
      value={value}
      onChange={(e) => onChange(e.target.value as OperationalStatus)}
    >
      {OPERATIONAL_STATUSES.map((s) => (
        <option key={s} value={s}>
          {t(`cat.status.${s}`)}
        </option>
      ))}
    </Select>
  );
}
